/**
 * Modernisiertes Cash-X Dashboard
 * 
 * Design: Glassmorphism, Bento Grid, Teal/Emerald Akzente
 * Features: Live Stats, Quick Actions, Feature Highlights
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Clock,
  ChefHat, QrCode, Gift, BarChart3, Printer, Settings, Plus,
  RefreshCw, AlertTriangle, ExternalLink, ArrowRight, Sparkles,
  Zap, Target, Brain, Receipt, Truck, CreditCard
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import SoftLockBanner from '../components/SoftLockBanner';
import BillingWarningBanner from '../components/BillingWarningBanner';
import AppSwitcher from '../components/AppSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Modern Stat Card with Glassmorphism
const StatCard = ({ title, value, change, icon: Icon, trend, accentColor = "teal" }) => {
  const accentStyles = {
    teal: "from-teal-500/20 to-teal-500/5 border-teal-500/20",
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/20",
    blue: "from-blue-500/20 to-blue-500/5 border-blue-500/20",
    amber: "from-amber-500/20 to-amber-500/5 border-amber-500/20"
  };
  
  const iconBg = {
    teal: "bg-teal-500/15 text-teal-400",
    emerald: "bg-emerald-500/15 text-emerald-400",
    blue: "bg-blue-500/15 text-blue-400",
    amber: "bg-amber-500/15 text-amber-400"
  };

  return (
    <Card className={`
      relative overflow-hidden
      bg-gradient-to-br ${accentStyles[accentColor]}
      bg-[#111C32]/80 backdrop-blur-xl
      border border-slate-700/50
      hover:border-slate-600/50 transition-all duration-300
      hover:-translate-y-[2px] hover:shadow-lg hover:shadow-black/20
    `}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">{title}</p>
            <p className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {value}
            </p>
            {change !== undefined && (
              <div className="flex items-center gap-1.5 mt-1">
                {trend === 'up' ? (
                  <div className="flex items-center gap-1 text-emerald-400">
                    <TrendingUp className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">{change}</span>
                  </div>
                ) : trend === 'down' ? (
                  <div className="flex items-center gap-1 text-red-400">
                    <TrendingDown className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">{change}</span>
                  </div>
                ) : (
                  <span className="text-xs text-slate-500">{change}</span>
                )}
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl ${iconBg[accentColor]} flex items-center justify-center`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
      {/* Decorative gradient line at bottom */}
      <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-${accentColor}-400/50 to-transparent`} />
    </Card>
  );
};

// Quick Action Button with modern styling
const QuickAction = ({ icon: Icon, label, onClick, color = "teal", testId }) => {
  const colors = {
    teal: "bg-teal-500/10 hover:bg-teal-500/20 border-teal-500/30 text-teal-400 hover:text-teal-300",
    emerald: "bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/30 text-emerald-400 hover:text-emerald-300",
    amber: "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/30 text-amber-400 hover:text-amber-300",
    blue: "bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/30 text-blue-400 hover:text-blue-300"
  };
  
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`
        group flex flex-col items-center justify-center p-4 rounded-xl border
        transition-all duration-200 hover:-translate-y-[2px]
        ${colors[color]}
      `}
    >
      <Icon className="w-6 h-6 mb-2 transition-transform group-hover:scale-110" />
      <span className="text-xs font-medium text-center">{label}</span>
    </button>
  );
};

export default function DashboardHome() {
  const navigate = useNavigate();
  const [sessionToken] = useState(() => localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  
  // Soft Lock Warning State (Legacy)
  const [softLockWarning, setSoftLockWarning] = useState(null);
  
  // Billing Warning State (Neues System)
  const [billingWarning, setBillingWarning] = useState(null);
  
  // Lade Billing Warning aus localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem('cashx_billing_warning_dismissed');
    const dismissedAt = localStorage.getItem('cashx_billing_warning_dismissed_at');
    
    // Dismiss-Status gilt max. 24 Stunden (um bei Status-Änderung wieder zu warnen)
    if (dismissed === 'true' && dismissedAt) {
      const dismissedTime = new Date(dismissedAt);
      const now = new Date();
      const hoursSinceDismiss = (now - dismissedTime) / (1000 * 60 * 60);
      
      if (hoursSinceDismiss < 24) {
        // Noch innerhalb 24h → nicht anzeigen (außer Soft Lock!)
        const warning = localStorage.getItem('cashx_billing_warning');
        if (warning) {
          const parsed = JSON.parse(warning);
          // Soft Lock wird IMMER angezeigt
          if (parsed.type === 'soft_locked') {
            setBillingWarning(parsed);
          }
        }
        return;
      }
    }
    
    // Lade neue Billing Warning
    const warning = localStorage.getItem('cashx_billing_warning');
    if (warning) {
      try {
        setBillingWarning(JSON.parse(warning));
      } catch (e) {
        console.error('Failed to parse billing warning:', e);
      }
    }
    
    // Legacy: Soft Lock Warning (Kompatibilität)
    const legacyWarning = localStorage.getItem('cashx_soft_lock_warning');
    if (legacyWarning && !warning) {
      try {
        const parsed = JSON.parse(legacyWarning);
        // Konvertiere zu neuem Format
        setBillingWarning({
          type: 'soft_locked',
          message: parsed.message,
          days_overdue: 0,
          days_until_lock: 0,
          can_sell: false
        });
      } catch (e) {
        console.error('Failed to parse legacy soft lock warning:', e);
      }
    }
  }, []);
  
  const handleDismissBillingWarning = () => {
    // Soft Lock kann NICHT dismissed werden
    if (billingWarning?.type === 'soft_locked') return;
    
    setBillingWarning(null);
    localStorage.setItem('cashx_billing_warning_dismissed', 'true');
    localStorage.setItem('cashx_billing_warning_dismissed_at', new Date().toISOString());
  };
  
  // Stats
  const [todayStats, setTodayStats] = useState({
    revenue: 0,
    orders: 0,
    avgOrder: 0,
    openOrders: 0
  });
  const [comparison, setComparison] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const loadData = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const headers = { 'Authorization': `Bearer ${sessionToken}` };
      
      // Load dashboard data
      const dashRes = await fetch(`${BACKEND_URL}/api/analytics/dashboard?period=today`, { headers });
      
      if (dashRes.ok) {
        const data = await dashRes.json();
        setTodayStats({
          revenue: data.sales?.total_revenue || 0,
          orders: data.sales?.total_orders || 0,
          avgOrder: data.sales?.average_order_value || 0,
          openOrders: 0
        });
        setComparison(data.sales?.comparison_percent);
      }
      
      // Load KDS stats for open orders
      const kdsRes = await fetch(`${BACKEND_URL}/api/kds/orders`, { headers });
      if (kdsRes.ok) {
        const kdsData = await kdsRes.json();
        setTodayStats(prev => ({
          ...prev,
          openOrders: kdsData.count || 0
        }));
        
        // Alerts for old orders
        const alertOrders = (kdsData.orders || []).filter(o => o.is_alert);
        if (alertOrders.length > 0) {
          setAlerts([{
            type: 'warning',
            message: `${alertOrders.length} Bestellung(en) warten länger als 10 Minuten!`
          }]);
        }
      }
      
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 animate-spin text-teal-400" />
          </div>
          <p className="text-slate-400 text-sm">Dashboard wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      {/* Background decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-32 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative p-4 md:p-6 lg:p-8">
        {/* Billing Warning Banner (Neues faires System) */}
        <BillingWarningBanner 
          billingWarning={billingWarning}
          onDismiss={handleDismissBillingWarning}
        />
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 
              className="text-2xl sm:text-3xl font-bold text-white tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              data-testid="dashboard-title"
            >
              {getTimeGreeting()}! 
            </h1>
            <p className="text-slate-400 mt-1">
              {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <div className="flex gap-2">
            <AppSwitcher />
            <Button 
              data-testid="refresh-dashboard-button"
              onClick={loadData}
              variant="outline" 
              size="sm"
              className="border-slate-700/70 bg-slate-800/30 text-slate-300 hover:bg-slate-700/50 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="mb-6">
            {alerts.map((alert, idx) => (
              <div 
                key={idx}
                className="
                  bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 
                  flex items-center gap-3 backdrop-blur-sm
                "
              >
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-amber-200 flex-1 text-sm">{alert.message}</p>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => navigate('/kds')}
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                >
                  KDS öffnen
                  <ExternalLink className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Stats Grid - Bento Layout */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Umsatz heute"
            value={formatCurrency(todayStats.revenue)}
            change={comparison ? `${comparison > 0 ? '+' : ''}${comparison.toFixed(0)}% vs. gestern` : undefined}
            trend={comparison > 0 ? 'up' : comparison < 0 ? 'down' : undefined}
            icon={DollarSign}
            accentColor="teal"
          />
          <StatCard
            title="Bestellungen"
            value={todayStats.orders.toString()}
            icon={ShoppingCart}
            accentColor="emerald"
          />
          <StatCard
            title="Ø Bon"
            value={formatCurrency(todayStats.avgOrder)}
            icon={Receipt}
            accentColor="blue"
          />
          <StatCard
            title="Offene Bestellungen"
            value={todayStats.openOrders.toString()}
            icon={Clock}
            accentColor="amber"
          />
        </div>

        {/* Quick Actions */}
        <Card className="bg-[#111C32]/80 backdrop-blur-xl border-slate-700/50 mb-8">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <Zap className="w-5 h-5 text-teal-400" />
              Schnellzugriff
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
              <QuickAction
                testId="quick-action-kasse"
                icon={ShoppingCart}
                label="Neue Bestellung"
                onClick={() => navigate('/app/kassier')}
                color="emerald"
              />
              <QuickAction
                testId="quick-action-kds"
                icon={ChefHat}
                label="Küchen-Display"
                onClick={() => window.open('/kds', '_blank')}
                color="amber"
              />
              <QuickAction
                testId="quick-action-qr"
                icon={QrCode}
                label="QR-Codes"
                onClick={() => navigate('/app/qr-codes')}
                color="blue"
              />
              <QuickAction
                testId="quick-action-loyalty"
                icon={Gift}
                label="Loyalty"
                onClick={() => navigate('/app/loyalty')}
                color="teal"
              />
              <QuickAction
                testId="quick-action-analytics"
                icon={BarChart3}
                label="Analytics"
                onClick={() => navigate('/app/analytics')}
                color="blue"
              />
              <QuickAction
                testId="quick-action-produkte"
                icon={Plus}
                label="Produkt"
                onClick={() => navigate('/app/produkte')}
                color="emerald"
              />
              <QuickAction
                testId="quick-action-bon"
                icon={Printer}
                label="Bon-Druck"
                onClick={() => navigate('/app/bon-einstellungen')}
                color="amber"
              />
              <QuickAction
                testId="quick-action-settings"
                icon={Settings}
                label="Einstellungen"
                onClick={() => navigate('/app/einstellungen')}
                color="teal"
              />
            </div>
          </CardContent>
        </Card>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Feature Highlights */}
          <Card className="bg-[#111C32]/80 backdrop-blur-xl border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <Sparkles className="w-5 h-5 text-teal-400" />
                Features entdecken
              </CardTitle>
              <CardDescription className="text-slate-400">
                Nutzen Sie alle Funktionen optimal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <button 
                data-testid="feature-ai-menu"
                onClick={() => navigate('/app/ai-menu-import')}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-teal-500/10 to-emerald-500/10 rounded-xl border border-teal-500/20 hover:border-teal-400/40 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center">
                  <Brain className="w-6 h-6 text-teal-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">KI-Menü-Import</p>
                  <p className="text-xs text-slate-400">Speisekarte automatisch importieren</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-teal-400 group-hover:translate-x-1 transition-all" />
              </button>
              
              <button 
                data-testid="feature-qr"
                onClick={() => navigate('/app/qr-codes')}
                className="w-full flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center">
                  <QrCode className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">QR-Tischbestellung</p>
                  <p className="text-xs text-slate-400">Gäste bestellen selbst</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
              </button>
              
              <button 
                data-testid="feature-loyalty"
                onClick={() => navigate('/app/loyalty')}
                className="w-full flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">Kundenbindung</p>
                  <p className="text-xs text-slate-400">Stempelkarten & Punkte</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
              </button>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card className="bg-[#111C32]/80 backdrop-blur-xl border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <Target className="w-5 h-5 text-emerald-400" />
                System-Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.5)]" />
                  <span className="text-slate-300">Backend</span>
                </div>
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">Online</Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.5)]" />
                  <span className="text-slate-300">Datenbank</span>
                </div>
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">Online</Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="text-slate-300">Drucker</span>
                </div>
                <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30">Nicht verbunden</Badge>
              </div>
              
              <div className="pt-4 border-t border-slate-700/50">
                <p className="text-xs text-slate-500 flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" />
                  Letzte Aktualisierung: {new Date().toLocaleTimeString('de-DE')}
                </p>
              </div>
            </CardContent>
          </Card>
          
        </div>
      </div>
    </div>
  );
}
